import { useEffect, useRef } from "react";
import { rootStore } from "stores/RootStore";

export const useTokenRefresh = () => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const scheduleNextRefresh = () => {
      const { authStore } = rootStore;

      if (!authStore.isAuthorized || !authStore.currentUser) {
        return;
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const timeUntilExpiry = authStore.timeUntilExpiry;
      const oneMinute = 60 * 1000; // 1 minute in milliseconds

      // Calculate when to refresh(1 minute before expiry)
      let delay: number;

      if (timeUntilExpiry > oneMinute) {
        // Token hasn't expired yet, schedule refresh 1 minute before expiry
        delay = timeUntilExpiry - oneMinute;
      } else if (timeUntilExpiry > 0) {
        // Token expires within 1 minute, refresh immediately
        delay = 0;
      } else {
        // Token is already expired, refresh immediately
        delay = 0;
      }

      // Ensure delay is not negative
      delay = Math.max(0, delay);

      // Schedule the refresh at the exact calculated time
      // This only works even when the browser tab is idle because setTimeout
      // It is scheduled for a specific time, not dependent on periodic checks
      timeoutRef.current = setTimeout(async () => {
        if (authStore.isAuthorized && authStore.shouldRefreshToken) {
          await authStore.refreshToken();
          // After refresh, schedule the next one based on new token expiry
          scheduleNextRefresh();
        }
      }, delay);
    };

    const checkAndRefreshToken = async () => {
      const { authStore } = rootStore;

      if (!authStore.isAuthorized) {
        return;
      }

      // Check if token expires within 1 minute
      if (authStore.shouldRefreshToken) {
        const refreshSuccess = await authStore.refreshToken();
        // Only reschedule if refresh was successful
        if (refreshSuccess) {
          scheduleNextRefresh();
        }
        // If refresh failed, logout will be handled by refreshToken
      } else {
        // Token doesn't need refresh yet, but reschedule to ensure we catch it
        scheduleNextRefresh();
      }
    };

    // Initial schedule - this calculates the exact time and schedules it
    // This ensures token refresh happens even when system is idle
    scheduleNextRefresh();

    // Backup interval check (every 30 seconds) - less critical now but good as backup
    intervalRef.current = setInterval(checkAndRefreshToken, 30000);

    // Handle browser tab visibility changes (user returns after being away)
    const handleVisibilityChange = async () => {
      if (!document.hidden && rootStore.authStore.isAuthorized) {
        // User came back to the tab - reschedule refresh immediately
        scheduleNextRefresh();
        // Also do an immediate check
        await checkAndRefreshToken();
      }
    };

    // Handle window focus (user switches back to the window)
    const handleFocus = async () => {
      if (rootStore.authStore.isAuthorized) {
        // Reschedule refresh immediately
        scheduleNextRefresh();
        // Also do an immediate check
        await checkAndRefreshToken();
      }
    };

    // Add event listeners for visibility and focus
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  return null;
};
